import { registerSW } from "virtual:pwa-register";

/**
 * Service worker registration, done once at module scope.
 *
 * `registerType: "prompt"` means the new worker installs but waits. Nothing
 * swaps under the user until they accept, which is the only sane behaviour for
 * an app whose main screen is a text field people are mid-sentence in.
 *
 * Kept out of a hook on purpose: StrictMode double-invokes effects, and
 * registering twice produces two `updateSW` callbacks fighting over one worker.
 */

export type SwStatus = {
  needRefresh: boolean;
  offlineReady: boolean;
};

let status: SwStatus = { needRefresh: false, offlineReady: false };
const listeners = new Set<() => void>();

function set(next: Partial<SwStatus>): void {
  status = { ...status, ...next };
  for (const l of listeners) l();
}

const updateSW = registerSW({
  immediate: true,
  onNeedRefresh() {
    set({ needRefresh: true });
  },
  onOfflineReady() {
    set({ offlineReady: true });
  },
  onRegistered(registration) {
    if (registration) watchForNewVersions(registration);
  },
  onRegisterError(error) {
    console.error("[pwa] service worker registration failed", error);
  },
});

/** How often an open app asks whether it is still the current one. */
const VERSION_CHECK_MS = 30 * 60 * 1000;

/**
 * Ask again when the app comes back, and now and then while it is open.
 *
 * A browser checks for a new worker on navigation, and an installed PWA that
 * lives on a home screen for days navigates approximately never. So the app
 * only ever learned about a deploy by being closed and opened — which is the
 * other half of «обновления приходят, только когда я перезагружаю приложение»,
 * and the half that had nothing to do with the data.
 *
 * Throttled, and only while the screen is on: this is one conditional request
 * against our own server, and the thing it is competing with is a battery.
 */
function watchForNewVersions(registration: ServiceWorkerRegistration): void {
  let askedAt = 0;

  const ask = () => {
    if (document.visibilityState === "hidden") return;

    const now = Date.now();
    if (now - askedAt < VERSION_CHECK_MS) return;
    askedAt = now;

    // Swallowed: offline, a worker mid-install, a server having a bad minute.
    // The banner simply does not appear this time round.
    void registration.update().catch(() => undefined);
  };

  document.addEventListener("visibilitychange", ask);
  window.addEventListener("focus", ask);
  setInterval(ask, VERSION_CHECK_MS);
}

export function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getStatus(): SwStatus {
  return status;
}

/** Activates the waiting worker and reloads into it. */
export async function applyUpdate(): Promise<void> {
  await updateSW(true);
}

export function dismissOfflineReady(): void {
  set({ offlineReady: false });
}

export function dismissUpdate(): void {
  set({ needRefresh: false });
}
